"use client";

import { useEffect, useRef, useState } from "react";
import styles from "./editorial.module.css";

type Heading = { id: string; title: string };

export function ArticleNavigation() {
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [active, setActive] = useState("");
  const progress = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const article = document.getElementById("article-text");
    if (!article) return;
    const elements = Array.from(
      article.querySelectorAll<HTMLElement>("h2[id]"),
    );
    setHeadings(
      elements.map((element) => ({
        id: element.id,
        title: element.textContent ?? "",
      })),
    );
    let frame = 0;
    const update = () => {
      frame = 0;
      const box = article.getBoundingClientRect();
      const distance = Math.max(1, box.height - window.innerHeight + 100);
      if (progress.current)
        progress.current.style.transform = `scaleX(${Math.max(0, Math.min(1, (100 - box.top) / distance))})`;
      let current: HTMLElement | undefined;
      for (const element of elements) {
        if (element.getBoundingClientRect().top <= 145) current = element;
      }
      setActive(current?.id ?? "");
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(schedule);
    observer.observe(article);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    update();
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, []);
  const links = (
    <nav aria-label="On this page">
      {headings.map((heading) => (
        <a
          key={heading.id}
          href={`#${heading.id}`}
          aria-current={active === heading.id ? "location" : undefined}
        >
          {heading.title}
        </a>
      ))}
    </nav>
  );
  return (
    <aside className={styles.contents}>
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: "0 0 auto",
          zIndex: 60,
          height: 2,
          pointerEvents: "none",
        }}
      >
        <div
          ref={progress}
          style={{
            height: "100%",
            background: "var(--theme-dark)",
            transform: "scaleX(0)",
            transformOrigin: "left",
          }}
        />
      </div>
      <div className={styles.desktopContents}>
        <span className={styles.label}>In this article</span>
        {links}
      </div>
      {headings.length > 0 ? (
        <details className={styles.mobileContents}>
          <summary>In this article</summary>
          {links}
        </details>
      ) : null}
    </aside>
  );
}
